import type { ReactNode } from 'react';

const rankBadgeStyles: Record<number, string> = {
  1: 'bg-rank-gold text-background',
  2: 'bg-rank-silver text-background',
  3: 'bg-rank-bronze text-background',
};

export const RankBadge = ({ rank }: { rank: number }) => {
  const style = rankBadgeStyles[rank] ?? 'bg-card-strong text-muted-foreground';
  return (
    <div
      className={`flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-xl text-sm font-black ${style}`}
      aria-hidden="true"
    >
      {rank}
    </div>
  );
};

export const rankRowClassName = (rank: number) =>
  `rounded-2xl py-[11px] pl-3 pr-3.5 transition-colors ${
    rank === 1 ? 'bg-card-strong border border-rank-gold' : 'bg-card border border-transparent'
  }`;

interface RankRowProps {
  rank: number;
  title: ReactNode;
  subtitle?: ReactNode;
  value: ReactNode;
  valueSub?: ReactNode;
  children?: ReactNode;
}

/** Shared row pattern for player and tournament rankings. */
export const RankRow = ({ rank, title, subtitle, value, valueSub, children }: RankRowProps) => (
  <div className={rankRowClassName(rank)}>
    <div className="flex items-center gap-3">
      <RankBadge rank={rank} />

      <div className="min-w-0 flex-1">
        <div className="truncate text-base font-bold text-foreground">{title}</div>
        {subtitle && (
          <div className="mt-0.5 truncate text-xs text-muted-foreground">{subtitle}</div>
        )}
      </div>

      <div className="flex-shrink-0 text-right">
        <div className="text-score text-xl leading-none text-foreground">{value}</div>
        {valueSub && <div className="mt-1 text-xs text-muted-foreground">{valueSub}</div>}
      </div>
    </div>
    {children}
  </div>
);
